import { useEffect } from 'react';
import { AlertTriangle, LogOut, RefreshCw } from 'lucide-react';

/**
 * Session Warning Modal
 * 
 * Displays a warning when user has been inactive for 14 minutes.
 * Shows countdown timer and options to stay logged in or logout.
 * 
 * Props:
 * - show: boolean - Whether to display the modal
 * - countdown: number - Seconds remaining before auto-logout
 * - onStayLoggedIn: function - Handler for "Stay Logged In" button
 * - onLogoutNow: function - Handler for "Logout Now" button
 */

function SessionModal({ show, countdown, onStayLoggedIn, onLogoutNow }) {
  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  // Don't render if not shown
  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 animate-fade-in"
        onClick={onStayLoggedIn}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div 
          className="bg-navy-light border-2 border-gold/50 rounded-2xl shadow-2xl max-w-md w-full animate-slide-up"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-gold/20">
            <div className="flex items-center space-x-3">
              <div className="bg-gold/20 p-3 rounded-full animate-pulse">
                <AlertTriangle className="text-gold" size={28} />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold to-gold-light">
                  Session Timeout Warning
                </h2>
                <p className="text-sm text-textLight/60 font-body mt-1">
                  Your session is about to expire
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-6">
            <p className="text-textLight/90 font-body text-base leading-relaxed mb-6">
              You've been inactive for a while. You'll be logged out in{' '}
              <span className="font-bold text-gold text-xl">{countdown}</span>{' '}
              {countdown === 1 ? 'second' : 'seconds'} unless you continue.
            </p>

            {/* Countdown Progress Bar */}
            <div className="mb-6">
              <div className="bg-navy-dark rounded-full h-2 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-gold to-gold-light h-full transition-all duration-1000 ease-linear"
                  style={{ width: `${(countdown / 60) * 100}%` }}
                />
              </div>
              <p className="text-xs text-textLight/50 text-center mt-2 font-body">
                {countdown > 30 ? 'Please click below to stay logged in' : 'Logging out soon...'}
              </p>
            </div>

            {/* Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Stay Logged In */}
              <button
                onClick={onStayLoggedIn}
                className="flex items-center justify-center space-x-2 bg-gradient-to-r from-teal to-teal-light hover:from-teal-light hover:to-teal text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg hover:shadow-teal/40 font-heading group"
              >
                <RefreshCw size={20} className="group-hover:rotate-180 transition-transform duration-500" />
                <span>Stay Logged In</span>
              </button>

              {/* Logout Now */}
              <button
                onClick={onLogoutNow}
                className="flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg hover:shadow-red-600/40 font-heading group"
              >
                <LogOut size={20} className="group-hover:translate-x-1 transition-transform" />
                <span>Logout Now</span>
              </button>
            </div>
          </div>

          {/* Footer Note */}
          <div className="px-6 pb-6">
            <p className="text-xs text-textLight/40 text-center font-body">
              💡 Tip: Move your mouse or press any key to reset the timer automatically
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default SessionModal;
